import React from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {useParams} from 'react-router';

import {skipToken} from '@reduxjs/toolkit/query';

import {ActionButton, Button} from '@edna/components';
import {Icon, Stack} from '@edna/components/primitives';
import CloseIcon from '@edna/icons/close.svg';
import EditIcon from '@edna/icons/edit.svg';
import {ECurrency, ECurrencySymbol, and} from '@edna/utils';

import {
  ButtonContent,
  InlineLink,
  InlineLinkButton,
  SecondaryText,
  Text,
} from 'src/components/primitives';
import {Banner} from 'src/containers/Banner';
import {useGetSupportClientQuery} from 'src/containers/SupportClients/api';
import {EPermission, useGetTenantDataQuery, usePermission} from 'src/containers/User';
import {CoinsIcon} from 'src/icons';
import {parseISO} from 'src/utils';

import {useGetStripeAutopaymentInfoQuery, useRestoreStripeAutopaymentMutation} from '../../api';
import {
  EAutopaymentError,
  EAutopaymentStatus,
  TAutopaymentError,
  TStripeAutopayment,
} from '../../definitions';
import * as S from '../../style';
import {useAutopaymentRecommendedThreshold} from '../useAutopaymentRecommendedThreshold';
import {
  showAutopaymentSupportModal,
  showChangeAutopaymentCreditsModal,
  showChargeAutopaymentModal,
  showOffAutopaymentModal,
} from './Modals';
import {showStripeSettingsModal} from './StripeSettingsModal';

const getFormattedDate = (date: string) => parseISO(date, 'dd.MM.yyyy');

const Error = React.memo<{error: TAutopaymentError}>(({error}) => {
  const {t} = useTranslation();

  if (error.declineCode === EAutopaymentError.GENERIC_DECLINE) {
    return null;
  }

  if (error.declineCode === EAutopaymentError.INSUFFICIENT_FUNDS) {
    return (
      <Banner type="ERROR" fontSize="S" noMargin>
        <Trans
          i18nKey={`BalanceReplenishment:errors.${error.declineCode}`}
          values={{
            date: getFormattedDate(error.occurredAt),
            nextDate: getFormattedDate(error.nextAttemptAt),
            leftDays: error.attemptsLeft,
            leftDays1: t('Common:time.days', {count: error.attemptsLeft}),
            leftDays2: t('Common:time_alternate.days', {count: error.attemptsLeft}),
          }}
        />
      </Banner>
    );
  }

  return (
    <Banner type="ERROR" fontSize="S" noMargin>
      <Trans
        i18nKey={`BalanceReplenishment:errors.${error.declineCode}`}
        values={{date: getFormattedDate(error.occurredAt)}}
      >
        <InlineLink href={error.hostedInvoiceUrl} />
      </Trans>
    </Banner>
  );
});

const SupportActions = React.memo(({isAutopaymentPaused}: {isAutopaymentPaused: boolean}) => {
  const {id: supportTenantId} = useParams<'id'>();
  const {t} = useTranslation();
  const hasPermissions = usePermission(
    and<EPermission>('PERMISSION_TENANT_BALANCE_ADD', 'PERMISSION_TENANT_BALANCE_SUBTRACT'),
  );

  const [restoreAutopayment] = useRestoreStripeAutopaymentMutation();

  const handleRestore = React.useCallback(
    () => restoreAutopayment(supportTenantId),
    [restoreAutopayment, supportTenantId],
  );

  if (isAutopaymentPaused) {
    return hasPermissions ? (
      <Button onClick={handleRestore} size="S">
        <ButtonContent>
          <Icon as={CoinsIcon} size="12px" />
          {t('BalanceReplenishment:actions.restoreAutopayment')}
        </ButtonContent>
      </Button>
    ) : null;
  }

  return (
    <Stack direction="ROW" size="2">
      <Button onClick={() => showAutopaymentSupportModal(isAutopaymentPaused)} size="S">
        <ButtonContent>
          <Icon as={CloseIcon} size="12px" />
          {t(`BalanceReplenishment:actions.${isAutopaymentPaused ? 'connect' : 'off'}Autopayment`)}
        </ButtonContent>
      </Button>
      <Button onClick={showChargeAutopaymentModal} size="S">
        {t(`BalanceReplenishment:actions.chargeAutopayment`)}
      </Button>
    </Stack>
  );
});

const ClientActions = React.memo<{isAutopaymentPaused: boolean}>(({isAutopaymentPaused}) => {
  const {t} = useTranslation();

  const [restoreAutopayment] = useRestoreStripeAutopaymentMutation();

  const actionsOptions = React.useMemo(
    () => [
      {
        content: t('BalanceReplenishment:actions.changeAutopaymentSettings'),
        handler: () => showStripeSettingsModal(),
      },
      {
        content: t('BalanceReplenishment:actions.changeCardCredits'),
        handler: showChangeAutopaymentCreditsModal,
      },
      {
        content: t('BalanceReplenishment:actions.offAutopayment'),
        handler: showOffAutopaymentModal,
      },
    ],
    [t],
  );

  const handleRestore = React.useCallback(() => restoreAutopayment(), [restoreAutopayment]);

  if (isAutopaymentPaused) {
    return (
      <Button onClick={handleRestore} size="S">
        <ButtonContent>
          <Icon as={CoinsIcon} size="12px" />
          {t('BalanceReplenishment:actions.restoreAutopayment')}
        </ButtonContent>
      </Button>
    );
  }

  return (
    <ActionButton options={actionsOptions} size="XS" appearance="default">
      <EditIcon />
      {t('BalanceReplenishment:actions.changeAutopaymentSettings')}
    </ActionButton>
  );
});

const AutopaymentInfo = React.memo<{info: TStripeAutopayment}>(({info}) => {
  const {t} = useTranslation();
  const {id: supportTenantId} = useParams<'id'>();

  const {data} = useGetTenantDataQuery();

  const {data: supportClient} = useGetSupportClientQuery(supportTenantId ?? skipToken);
  const recommendedThreshold = useAutopaymentRecommendedThreshold();

  const currency =
    (supportTenantId ? supportClient?.defaultCurrency : data?.defaultCurrency) ?? ECurrency.USD;

  const isAutopaymentPaused = info.customerStatus === EAutopaymentStatus.DISABLED;

  const handleChangeSettings = React.useCallback(() => showStripeSettingsModal(), []);

  return (
    <Stack size="4">
      <Stack size="2" direction="ROW" align="center">
        <Text weight={500} color={isAutopaymentPaused ? 'red500' : 'grey900'}>
          <S.SecondaryCoinsIcon />
          {t(
            `BalanceReplenishment:autopaymentStatus.${isAutopaymentPaused ? 'paused' : 'connected'}`,
          )}
        </Text>
        {!!supportTenantId && <SupportActions isAutopaymentPaused={isAutopaymentPaused} />}
        {!supportTenantId && <ClientActions isAutopaymentPaused={isAutopaymentPaused} />}
      </Stack>
      <S.NestedBlock>
        {info.threshold < recommendedThreshold && (
          <Banner type="WARNING" fontSize="S" noMargin>
            <Trans i18nKey="BalanceReplenishment:thresholdWarning">
              <InlineLinkButton onClick={handleChangeSettings} />
            </Trans>
          </Banner>
        )}
        {!!info.paymentError && <Error error={info.paymentError} />}
        <SecondaryText size="S">
          <Trans
            i18nKey="BalanceReplenishment:stripeAutopaymentInfo"
            values={{
              currency: ECurrencySymbol[currency],
              threshold: info.threshold,
              amount: info.amount,
            }}
          >
            <Text color="grey400" isInline size="S" />
            <Text isInline size="S" />
            <Text color="blue500" isInline size="S" />
          </Trans>
        </SecondaryText>
      </S.NestedBlock>
    </Stack>
  );
});

const StripeAutopaymentInfo = React.memo(() => {
  const {id: supportTenantId} = useParams<'id'>();

  const {data: info} = useGetStripeAutopaymentInfoQuery(supportTenantId);

  if (!info) {
    return null;
  }

  return <AutopaymentInfo info={info} />;
});

export {StripeAutopaymentInfo};
